import { ReactElement } from 'react'
import {
  BaseColumnEntry,
  ColumnSettings,
  ColumnSettingsAtom,
} from 'ui-components'

import { TaskCellProps } from './task-cells/task-cell'
import { TaskSortKey } from './task-sorters'
import { TaskValueExtractor } from './column-values/value-extractors'
import makeGenericTaskCell from './task-cells/make-generic-task-cell'
import TaskTypeCell from './task-cells/task-type-cell'
import MenuTaskCell from './task-cells/menu-task-cell'
import TaskTestRunCell from './task-cells/task-test-run-cell'
import TaskSelectCell from './task-cells/task-select-cell'
import TaskAgeCell from './task-cells/task-age-cell'
import TaskActionsUrlCell from './task-cells/task-actions-url-cell'

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never
export const UTILITIES_COLUMN = '_UTILITY_MENU' as const
export const TASK_SELECT_COLUMN = '_TASK_SELECT' as const

export const COLUMN_ORDER_TASKS = [
  'taskType',
  'menuTask',
  'companyName',
  'assignee',
  'taskActions',
  'configPriority',
  'configScope',
  'configScrapeStrategy',
  'title',
  'lastModifiedTime',
  'taskLastModifiedBy',
  'scheduledTime',
  'creationTime',
  'id',
  'configUuid',
  'taskAge',
] as const

export type TaskColumnId =
  | ArrayElement<typeof COLUMN_ORDER_TASKS>
  | '_UTILITY_MENU'
  | '_TASK_SELECT'

export type NormalTaskColumn = Exclude<TaskColumnId, typeof UTILITIES_COLUMN>

export const isNormalTaskColumn = (input: unknown): input is NormalTaskColumn =>
  (COLUMN_ORDER_TASKS as readonly unknown[]).includes(input) &&
  input !== UTILITIES_COLUMN &&
  input !== TASK_SELECT_COLUMN

export type TaskCell = (props: TaskCellProps) => ReactElement | null

export type TaskColumnSpecs = Record<
  Exclude<TaskColumnId, typeof UTILITIES_COLUMN>,
  {
    heading: string
    Cell: TaskCell
    sortValueKey: TaskSortKey
    tsvValueExtractor?: TaskValueExtractor // custom value extractor to override extractor based on sortValueKey
    minWidthPx?: number
  }
> & {
  [UTILITIES_COLUMN]: {
    minWidthPx?: number
    heading: string
  }
}
export const TASK_COLUMN_SPECS: TaskColumnSpecs = {
  [TASK_SELECT_COLUMN]: {
    heading: '',
    sortValueKey: 'unsortable', // not used
    Cell: TaskSelectCell,
    minWidthPx: 15,
  },
  taskType: {
    heading: 'Type',
    Cell: TaskTypeCell,
    sortValueKey: 'type',
    minWidthPx: 55,
  },
  menuTask: {
    heading: '',
    Cell: MenuTaskCell,
    sortValueKey: 'unsortable',
    minWidthPx: 55,
  },
  companyName: {
    heading: 'Company Name',
    Cell: makeGenericTaskCell('companyName'),
    sortValueKey: 'companyName',
    minWidthPx: 200,
  },
  taskActions: {
    heading: 'Actions',
    Cell: TaskActionsUrlCell,
    sortValueKey: 'unsortable',
    minWidthPx: 100,
  },
  id: {
    heading: 'Task Id',
    Cell: makeGenericTaskCell('id'),
    sortValueKey: 'id',
    minWidthPx: 200,
  },
  creationTime: {
    heading: 'Created',
    Cell: makeGenericTaskCell('creationTime'),
    sortValueKey: 'creationTime',
    minWidthPx: 170,
  },
  lastModifiedTime: {
    heading: 'Last Modified',
    Cell: makeGenericTaskCell('lastModifiedTime'),
    sortValueKey: 'lastModifiedTime',
    minWidthPx: 170,
  },
  taskLastModifiedBy: {
    heading: 'Last Modified By',
    Cell: makeGenericTaskCell('lastModifiedBy'),
    sortValueKey: 'lastModifiedBy',
    minWidthPx: 170,
  },
  scheduledTime: {
    heading: 'Scheduled',
    Cell: makeGenericTaskCell('scheduledTime'),
    sortValueKey: 'scheduledTime',
    minWidthPx: 170,
  },
  assignee: {
    heading: 'Assignee',
    Cell: makeGenericTaskCell('assignee'),
    sortValueKey: 'assignee',
    minWidthPx: 200,
  },
  configScope: {
    heading: 'Scope',
    Cell: makeGenericTaskCell('configScope'),
    sortValueKey: 'configScope',
    minWidthPx: 80,
  },
  configScrapeStrategy: {
    heading: 'Strategy',
    Cell: makeGenericTaskCell('configScrapeStrategy'),
    sortValueKey: 'configScrapeStrategy',
    minWidthPx: 150,
  },
  configUuid: {
    heading: 'Config UUID',
    Cell: makeGenericTaskCell('configUuid'),
    sortValueKey: 'configUuid',
    minWidthPx: 200,
  },
  title: {
    heading: 'Title',
    Cell: makeGenericTaskCell('title'),
    sortValueKey: 'title',
    minWidthPx: 400,
  },
  configPriority: {
    heading: 'Priority',
    Cell: makeGenericTaskCell('configPriority'),
    sortValueKey: 'configPriority',
    minWidthPx: 65,
  },
  taskAge: {
    heading: 'Age',
    minWidthPx: 85,
    sortValueKey: 'taskAge',
    Cell: TaskAgeCell,
  },
  [UTILITIES_COLUMN]: {
    heading: '',
    minWidthPx: 15,
  },
}

const taskBulkToggleKeys = [
  'showingAll' as const,
  'hidingAllExceptTaskIdAndConfigName' as const,
  'showingDefault' as const,
]
export type TaskBulkToggleKey = typeof taskBulkToggleKeys[number]

const taskOrderOptionKeys = ['standard' as const]
export type TaskOrderOptionKey = typeof taskOrderOptionKeys[number] | 'custom'

export type TaskColumnEntry = BaseColumnEntry<TaskColumnId> & {
  heading: string
  sortKey: TaskSortKey | null
  minWidthPx?: number
}

export type TaskColumnSettings = ColumnSettings<
  TaskColumnId,
  TaskColumnEntry,
  TaskBulkToggleKey,
  TaskOrderOptionKey
>
export type TaskColumnSettingsAtom = ColumnSettingsAtom<
  TaskColumnId,
  TaskColumnEntry,
  TaskBulkToggleKey,
  TaskOrderOptionKey
>
